import { useRouter } from "next/router";
import React, {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { HOME_PATH } from "modules/utils/Constants";

import { MESSAGE_THREAD_ID_QUERY_STRING } from "modules/hooks/use-query-string";
import { useAuth } from "../auth-provider";

interface Notification {
  displayNewMessageNotification: (props: {
    title: string;
    body?: string;
    messageThreadId: string;
  }) => void;
}

const NotificationContext = createContext<Notification>({
  displayNewMessageNotification: () => {},
});

export interface NotificationProps {
  children?: ReactNode;
}

export const NotificationProvider = memo<NotificationProps>(
  (notificationProps) => {
    const { isLoggedIn } = useAuth();
    const { push } = useRouter();

    const isSupported = useMemo(
      () =>
        typeof window !== "undefined" && // eliminate server side
        "Notification" in window, // iOS safari does not support Notification API
      [],
    );

    const audio = useMemo(() => {
      if (!isSupported) {
        return;
      }

      return new Audio("/sounds/notification.wav");
    }, [isSupported]);

    const notification = useMemo<Notification>(
      () => ({
        displayNewMessageNotification: ({ title, body, messageThreadId }) => {
          // client-side only
          if (!isSupported) {
            return;
          }

          if (Notification.permission !== "granted") {
            // eslint-disable-next-line no-console
            console.warn("Skipping notification - permissions not granted.");
            return;
          }

          audio?.play().catch((e) => {
            // eslint-disable-next-line no-console
            console.error("Failed to play audio notification", e);
          });

          new Notification(title, {
            body,
            icon:
              process.env.NEXT_PUBLIC_ENV_NAME === "prod"
                ? "/favicon.svg"
                : "/qa_favicon.svg",
          }).onclick = () => {
            push({
              pathname: HOME_PATH,
              query: { [MESSAGE_THREAD_ID_QUERY_STRING]: messageThreadId },
            });
          };
        },
      }),
      [audio, isSupported, push],
    );

    useEffect(() => {
      if (!isLoggedIn || !isSupported) {
        return;
      }

      Notification.requestPermission();
    }, [isLoggedIn, isSupported]);

    return (
      <NotificationContext.Provider value={notification}>
        {notificationProps.children}
      </NotificationContext.Provider>
    );
  },
);

export const useNotification = () => useContext(NotificationContext);
